<template>
  <div>
    <div class="top">
      <div
        class="top-title"
        @mouseenter="titleHover = true"
        @mouseleave="titleHover = false"
        @click="$router.push(`/predictions/heatmaps`)"
        v-if="!compareMode"
      >
        <bg-icon name="chevronLeft"></bg-icon>
        <div>
          <transition name="slide-fade">
            <span v-if="!titleHover">{{ heatmap.title }}</span>
          </transition>
          <transition name="slide-fade">
            <span style="font-weight: 500;" v-if="titleHover"
              >&#128072; back to heatmaps</span
            >
          </transition>
        </div>
      </div>
      <div class="right-top">
        <div style="display: flex; gap: 20px;">
          <div class="variants">
            <div
              v-bind:class="{ active: variant === 'Heatmap' }"
              class="v1 option"
              @click="
                variant = 'Heatmap';
                showBoxes = true;
              "
            >
              heatmap
            </div>
            <div
              v-bind:class="{ active: variant === 'Peekthrough' }"
              class="v2 option"
              @click="
                showBoxes = false;
                variant = 'Peekthrough';
              "
            >
              peekthrough
            </div>
          </div>
          <div
            class="buttons"
            style="display:flex; gap: 10px; min-width: 150px;"
          >
            <a
              class="button"
              :href="
                variant === 'Heatmap'
                  ? heatmap.image_result
                  : heatmap.peekthrough
              "
            >
              <div class="downloadbutton">
                <bg-icon name="save"></bg-icon>
              </div>
            </a>
            <div class="deletebutton" @click="deleteHeatmap">
              <bg-icon name="trash"></bg-icon>
            </div>
            <div
              @click="
                $router.push(
                  `/predictions/heatmaps/${$route.params.heatmapId}/compare`
                )
              "
              class="compare"
            >
              <bg-icon name="minimize"></bg-icon>
              <p>Compare heatmaps</p>
            </div>
          </div>
        </div>

        <div class="slider" v-if="!compareMode">
          <p>Opacity: {{ slider }}%</p>
          <input
            type="range"
            min="1"
            max="100"
            v-model="slider"
            class="slider"
            id="myRange"
          />
        </div>
      </div>
    </div>
    <div class="heatmapcontainer">
      <div class="info-container">
        <transition name="slide-down">
          <div
            class="aois"
            v-if="!isBoxesUpdating && !isLoading && variant === 'Heatmap'"
          >
            <div class="aoi-title">
              <h3 v-bg-typography="'h3'">
                Area's of interest
              </h3>
              <transition name="slide-fade">
                <p
                  v-if="boxes.length === 0"
                  style="text-align: center; margin-bottom: 10px;"
                >
                  Start drawing boxes on your heatmap to calculate the
                  percentage of attention.
                </p>
              </transition>
              <transition name="slide-fade">
                <img src="./aoi.svg" alt="" v-if="boxes.length === 0" />
              </transition>
              <div class="options">
                <!-- <p style="text-align: center; cursor: pointer; font-size: 10pt;">
                <u>Import AOI's</u>
              </p> -->
                <p
                  @click="showBoxes = !showBoxes"
                  style="text-align: center; cursor: pointer; font-size: 10pt;"
                >
                  <u v-if="showBoxes">Hide AOI's</u><u v-else>Show AOI's</u>
                </p>
              </div>
            </div>
            <div class="box-wrapper">
              <div
                v-for="(box, index) in filtered"
                :key="index"
                class="box"
                :style="{ width: '100%' }"
                @mouseover="boxHover = index"
                @mouseleave="boxHover = null"
                @click="selectAOI(index)"
                v-bind:class="{ active: activeBoxIndex === index }"
              >
                <p>
                  {{ box.name }}
                </p>
                <p style="color: #0931F0;">
                  <b>{{ Math.floor(box.value * 100) }}%</b>
                </p>

                <div class="predelete">
                  <div></div>
                  <transition name="slide-fade-right">
                    <div
                      class="delete"
                      v-if="boxHover === index"
                      @click="deleteBox(index)"
                    >
                      <bg-icon name="fdelete"></bg-icon>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
            <p
              style="text-align: center; cursor: pointer; font-size: 10pt;"
              @click="showAllBoxes = true"
              v-if="!showAllBoxes && boxes.length > 3"
            >
              <u>+ {{ boxes.length - 3 }} more</u>
            </p>
            <p
              style="text-align: center; cursor: pointer; font-size: 10pt;"
              v-if="showAllBoxes && boxes.length > 3"
              @click="showAllBoxes = false"
            >
              <u>hide</u>
            </p>
          </div>
        </transition>

        <div class="info legend" v-if="!isLoading">
          <h2 v-bg-typography="'h2'">
            Legend
          </h2>
          <transition name="slide-fade">
            <p v-if="variant === 'Heatmap'">
              Hot areas in your image are expected to receive the most
              attention.
            </p>
          </transition>
          <transition name="slide-fade">
            <p v-if="variant === 'Peekthrough'">
              The <span style="color: #141A77;"><b>blue</b> </span> area is not
              seen in the first 3-5 seconds.
            </p>
          </transition>
          <transition name="slide-fade">
            <div v-if="variant === 'Heatmap'">
              <div class="labels">
                <p>Hot</p>
                <p>Cold</p>
              </div>
              <div class="gradient"></div>
            </div>
          </transition>
        </div>

        <div class="info about" v-if="!isLoading">
          <h2 v-bg-typography="'h2'">
            About Predictions
          </h2>
          <img src="./predictions.svg" alt="" />
          <p>
            Predictions is an A.I. powered tool for predicting attention. The
            tool predicts with an accuracy of 94% where people will look the
            first 3-5 seconds they are faced with a visual/design. Hereby, you
            can quickly and easily assess if the visual hierarchy of the design
            is clear and whether crucial information is visually salient or not.
          </p>
          <br />
          <p style="cursor: pointer;" @click="openDocs">
            <u>Read a step-by-step-guide</u>
          </p>
        </div>
      </div>
      <div class="image-heatmap">
        <bg-loader :dark="true" v-if="isLoading"></bg-loader>
        <Heatmap
          v-if="!isLoading"
          :variant="variant"
          :showBoxes="showBoxes"
          ref="heatmap"
          :data="heatmapData"
          :loadBoxes="heatmap.areas_of_interest"
          :originalWidth="originalWidth"
          :image="heatmap.image"
          :image_result="heatmap.image_result"
          :peekthrough="heatmap.peekthrough"
          :heatmap="heatmap.heatmap"
          :opacity="slider"
          @onSelectBox="selectBox"
          @boxUpdate="updateBoxes"
          @delete="deleteAOI"
          @newBox="addAOI"
          @updateName="updateName"
          :key="rerenderKey"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BgButton from "@/components/bg-button";
import BgSelect from "@/components/bg-select";
import Heatmap from "./heat";
import BgTypography from "@/directives/bg-typography";
import BgIcon from "@/components/bg-icon";
import API from "@/api";
import BgLoader from "@/components/bg-loader";
import BgField from "@/components/bg-field";

export default {
  components: {
    BgButton,
    BgSelect,
    Heatmap,
    BgIcon,
    BgLoader,
    BgField,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      activeBoxIndex: null,
      boxes: [],
      boxHover: false,
      compareMode: false,
      compareHeatmap: null,
      data: [],
      heatmap: {},
      heatmapData: [],
      heatmapOptions: {
        backgroundColor: "transparent",
      },
      isBoxesUpdating: false,
      isLoading: false,
      options: [],
      optionsVariant: ["Original", "Heatmap", "Peekthrough"],
      originalWidth: null,
      rerenderKey: 0,
      selected: null,
      showAllBoxes: false,
      showHeatmap: true,
      showBoxes: true,
      showPeekthrough: false,
      slider: 30,
      width: 0,
      height: 0,
      mouseOver: false,
      titleHover: false,
      typing: false,
      variant: "Heatmap",
    };
  },
  async created() {
    this.isLoading = true;
    const result = await API.test.getPrediction(this.$route.params.heatmapId);
    const heatmaps = await API.test.getPredictions();

    this.heatmap = result;
    heatmaps.forEach((element) => {
      if (element.image) {
        this.options.push(element);
      }
    });
    let img = new Image();
    img.src = this.heatmap.image_result;
    img.onload = () => {
      this.width = img.width;
      this.heigth = img.heigth;
    };

    this.selected = result;
    this.createTestHeatmap(result);
  },
  watch: {
    activeBoxIndex: function(val) {
      if (this.activeBoxIndex > 2 && !this.showAllBoxes) {
        this.showAllBoxes = true;
      }
    },
  },
  computed: {
    filtered() {
      let filtered = this.boxes;

      if (!this.showAllBoxes) {
        filtered = filtered.slice(0, 3);
      }
      return filtered;
    },
    portraitImage() {
      return this.width < this.heigth;
    },
    boxesWidth() {
      if (this.portraitImage) {
        return "90%";
      } else {
        return "50%";
      }
    },
  },
  methods: {
    async addAOI(val) {
      console.log("add new box");
      const res = await API.test.createAOI(this.$route.params.heatmapId, val);
    },
    changeVariant(val) {
      this.variant = val;
    },
    async createTestHeatmap(val) {
      this.heatmapData = [];
      const imageOriginal = await fetch(val.image);
      const imgblobOriginal = await imageOriginal.blob();
      const imgOriginal = await createImageBitmap(imgblobOriginal);
      this.originalWidth = imgOriginal.width;

      const image = await fetch(val.image_mask);
      const imgblob = await image.blob();
      const img = await createImageBitmap(imgblob);
      var canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ratio = this.originalWidth / img.width;

      canvas.getContext("2d").drawImage(img, 0, 0, img.width, img.height);

      // this.heatmap.clarity = {
      //   x: [],
      //   y: [],
      // };

      for (let x = 0; x < img.width + 100; x++) {
        for (let y = 0; y < img.height + 100; y++) {
          var pixelData = canvas.getContext("2d").getImageData(x, y, 1, 1).data;
          const Y =
            0.2126 * pixelData[0] +
            0.7152 * pixelData[1] +
            0.0722 * pixelData[2];

          // if (Y > 150) {
          //   if (!this.heatmap.clarity.x.includes(x)) {
          //     this.heatmap.clarity.x.push(x);
          //   }
          //   if (!this.heatmap.clarity.y.includes(y)) {
          //     this.heatmap.clarity.y.push(y);
          //   }
          // }

          this.heatmapData.push({
            x: x * ratio,
            y: y * ratio,
            value: Y * (ratio / 100),
          });
        }
      }

      this.isLoading = false;
    },
    async deleteHeatmap() {
      let r = confirm("Are you sure?!");
      if (r == true) {
        const res = await API.test.deletePrediction(
          this.$route.params.heatmapId
        );
        this.$router.push(`/predictions/heatmaps`);
      }
    },
    deleteBox(i) {
      const child = this.$refs.heatmap;
      child.removeBox(i);
    },
    async deleteAOI(val) {
      if (val.id) {
        console.log("deleting preloaded");
        const res = await API.test.deleteAOI(
          this.$route.params.heatmapId,
          val.id
        );
        console.log(res);
      } else {
        console.log("deleting existing");
        const result = await API.test.getPrediction(
          this.$route.params.heatmapId
        );

        const del = await API.test.deleteAOI(
          this.$route.params.heatmapId,
          result.areas_of_interest.find(
            (el) =>
              Math.floor(el.top) === Math.floor(val.top) &&
              Math.floor(el.left) === Math.floor(val.left)
          ).id
        );
        console.log(del);
      }
    },
    getWidth: function(val) {
      return `${val * 100}%`;
    },
    async heatmapChange(val) {
      if (this.heatmap.id != val.id) {
        this.$router.push(`/predictions/heatmaps/${val.id}`);
      }
    },
    openInNewTab(url) {
      window.open(url, "_blank").focus();
    },
    openDocs() {
      window.open(
        "https://braingineers.crunch.help/get-started/get-started-with-predictions",
        "_blank"
      );
    },
    onSelect(val) {
      this.compareHeatmap = val;
    },
    selectAOI(i) {
      const child = this.$refs.heatmap;
      child.makeBoxActive(i);
    },
    selectBox(val) {
      this.activeBoxIndex = val;
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    updateBoxes(val) {
      this.isBoxesUpdating = true;
      this.activeBoxIndex = null;
      this.boxes = val;
      this.isBoxesUpdating = false;
    },
    async updateName(val) {
      if (val.id) {
        const update = await API.test.updateAOI(
          this.$route.params.heatmapId,
          val
        );
      } else {
        console.log("updating existing");
        const result = await API.test.getPrediction(
          this.$route.params.heatmapId
        );

        const box = result.areas_of_interest.find(
          (el) =>
            Math.floor(el.top) === Math.floor(val.top) &&
            Math.floor(el.left) === Math.floor(val.left)
        );
        const updated = {
          width: Math.floor(val.width),
          heigth: Math.floor(val.heigth),
          top: Math.floor(val.top),
          left: Math.floor(val.left),
          id: box.id,
          name: val.name,
        };
        const update = await API.test.updateAOI(
          this.$route.params.heatmapId,
          updated
        );
        console.log(update);
      }
    },
    async updatePrediction() {
      const res = await API.test.createAOI(
        this.heatmap.id,
        this.heatmap.areas_of_interest[0]
      );
      console.log(res);
    },
  },
};
</script>

<style scoped lang="scss">
@import "./styles.scss";
</style>
